import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Save, History, Loader2 } from "lucide-react";

export default function SnapshotControl({ projectId, scope, onRestored }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const { data } = useQuery({
    queryKey: ["snapshot", projectId, scope],
    queryFn: async () => (await api.get(`/projects/${projectId}/snapshot/${scope}`)).data,
  });
  const saved = data?.exists;
  const when = data?.created_at ? new Date(data.created_at).toLocaleString() : null;

  const save = async () => {
    setBusy("save");
    try {
      await api.post(`/projects/${projectId}/snapshot/${scope}`);
      qc.invalidateQueries({ queryKey: ["snapshot", projectId, scope] });
      toast.success("Baseline saved — you can revert to this anytime");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const restore = async () => {
    if (!window.confirm("Revert to your saved baseline? Current edits on this page will be replaced.")) return;
    setBusy("restore");
    try {
      await api.post(`/projects/${projectId}/snapshot/${scope}/restore`);
      toast.success("Reverted to saved baseline");
      if (onRestored) await onRestored();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`snapshot-${scope}`}>
      <button data-testid={`snapshot-save-${scope}`} onClick={save} disabled={!!busy} title={saved ? `Baseline saved ${when}` : "Save a baseline you can revert to"}
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
        {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {saved ? "Update baseline" : "Save baseline"}
      </button>
      {saved && (
        <button data-testid={`snapshot-restore-${scope}`} onClick={restore} disabled={!!busy} title={`Revert to baseline from ${when}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 text-amber-500 px-3 py-1.5 text-xs font-semibold hover:bg-amber-500/10 transition-colors disabled:opacity-60">
          {busy === "restore" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />} Revert to original
        </button>
      )}
    </div>
  );
}
